/**
 * The React bridge to `AppStore`.
 *
 * The store is deliberately framework-agnostic — it is a plain class with
 * `subscribe`/`state` — so this is the entire integration layer: one hook that
 * subscribes, and one context that hands the store to components. Nothing about
 * the store changed to make React work with it, which is the point: the RPC,
 * transcript and auth layers below it are untouched by the rewrite and keep
 * their tests.
 */

import { createContext, use, useSyncExternalStore, type ReactNode } from "react";
import type { AppSnapshot, AppStore } from "../app/store";

const StoreContext = createContext<AppStore | undefined>(undefined);

export function StoreProvider({
  store,
  children
}: {
  store: AppStore;
  children: ReactNode;
}) {
  return <StoreContext value={store}>{children}</StoreContext>;
}

/** The store itself, for actions. Re-renders nothing on its own. */
export function useStore(): AppStore {
  const store = use(StoreContext);
  if (store === undefined) throw new Error("useStore must be used inside StoreProvider");
  return store;
}

/**
 * The current snapshot, re-rendering on every change.
 *
 * `useSyncExternalStore` rather than `useState` + `useEffect`: the store can
 * notify during a render (a synchronous patch from an event handler that React
 * is already processing), and an effect-based subscription would miss that
 * notification and tear. This is the API built for exactly that hazard.
 */
export function useAppState(): AppSnapshot {
  const store = useStore();
  return useSyncExternalStore(
    (listener) => store.subscribe(listener),
    () => store.state
  );
}
